import { Game } from "./game/Game";
import { AudioManager } from "./ui/AudioManager";
import { UIOverlay } from "./ui/UIOverlay";
import { HomeScreen } from "./ui/HomeScreen";

const COMMON_PRELOAD_URLS = [
  "/sprites/tutorial/how_to_play_bg.png",
  "/sprites/tutorial/title.png",
  "/sprites/tutorial/flower.png",
  "/sprites/tutorial/card_bg.png",
  "/sprites/tutorial/close_btn_default.png",
  "/sprites/tutorial/close_btn_hover.png",
  "/sprites/tutorial/close_btn_pressed.png",
  "/sprites/tutorial/question_default.png",
  "/sprites/tutorial/question_hover.png",
  "/sprites/tutorial/question_pressed.png",
  "/sprites/tutorial/numbers/number_1.png",
  "/sprites/tutorial/numbers/number_2.png",
  "/sprites/tutorial/numbers/number_3.png",
  "/sprites/tutorial/numbers/number_4.png",
  "/sprites/tutorial/numbers/number_5.png",
];

const MOBILE_PRELOAD_URLS = [
  "/sprites/tutorial/how_to_play_bg_mob.png",
  "/sprites/tutorial/title_mob.png",
  "/sprites/tutorial/card_bg_mob.png",
  "/sprites/tutorial/numbers_mob/number_1_mob.png",
  "/sprites/tutorial/numbers_mob/number_2_mob.png",
  "/sprites/tutorial/numbers_mob/number_3_mob.png",
  "/sprites/tutorial/numbers_mob/number_4_mob.png",
  "/sprites/tutorial/numbers_mob/number_5_mob.png",
  "/sprites/out_of_funds/mob/bet_amount_bg_mob.png",
  "/sprites/out_of_funds/mob/title_mob.png",
  "/sprites/out_of_funds/mob/sadmonkey_mob.png",
];

const DESKTOP_PRELOAD_URLS = [
  "/sprites/out_of_funds/desktop/bet_amount.png",
  "/sprites/out_of_funds/desktop/title.png",
  "/sprites/out_of_funds/desktop/sadmonkey.png",
];

function preloadImages(urls: string[]): Promise<void> {
  return Promise.all(
    urls.map(async (url) => {
      const img = new Image();
      img.src = url;
      try {
        await img.decode();
      } catch {
        /* ignore */
      }
    }),
  ).then(() => undefined);
}

async function bootstrap() {
  const pixiRoot = requiredEl<HTMLDivElement>("pixi-root");
  const uiRoot = requiredEl<HTMLDivElement>("ui-root");
  const homeRoot = requiredEl<HTMLDivElement>("home-root");

  const game = new Game();
  const responsivePreloads = window.matchMedia("(max-width: 640px)").matches
    ? MOBILE_PRELOAD_URLS
    : DESKTOP_PRELOAD_URLS;
  await Promise.all([game.init(pixiRoot), preloadImages([...COMMON_PRELOAD_URLS, ...responsivePreloads])]);

  const ui = new UIOverlay(uiRoot, game);
  game.bindUI(ui);

  const audio = new AudioManager("/background.mp3");
  const home = new HomeScreen(homeRoot, game.getTextures(), audio);
  home.setBalance(game.getBalance());
  home.setBet(game.getBet());
  home.onBetChange((amount) => game.setBet(amount));
  home.onPlay(() => game.startRound());
  game.onTryAgainRequested(() => {
    home.setBalance(game.getBalance());
    home.setBet(game.getBet());
    home.show();
    if (game.getBalance() < game.getBet() || game.getBalance() < 1) {
      home.showOutOfFunds();
    }
  });

  window.addEventListener("resize", () => game.resize());
  window.visualViewport?.addEventListener("resize", () => game.resize());
  game.resize();
}

function requiredEl<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing #${id}`);
  return el as T;
}

function windowLoaded(): Promise<void> {
  if (document.readyState === "complete") return Promise.resolve();
  return new Promise((resolve) => window.addEventListener("load", () => resolve(), { once: true }));
}

function hidePreloader() {
  const el = document.getElementById("preloader");
  if (!el) return;
  el.classList.add("hidden");
  el.addEventListener("transitionend", () => el.classList.add("removed"), { once: true });
}

Promise.all([bootstrap(), windowLoaded()])
  .then(hidePreloader)
  .catch((err) => {
    console.error("Failed to boot:", err);
    const el = document.getElementById("preloader");
    if (el) el.remove();
    const pre = document.createElement("pre");
    pre.style.cssText = "color:#f88;padding:20px;white-space:pre-wrap";
    pre.textContent = String(err);
    document.body.appendChild(pre);
  });
